import { getSessionStorage } from 'nsdi-volto-frontend/utils/browserStorage';
import { environment } from 'nsdi-volto-frontend/config/environments';

let sessionId: string =
  globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
let boundSearchUrl: string | null = null;

/** Safe sessionStorage getter (no-op on SSR) */
export function getSessionToken(): string {
  try {
    return getSessionStorage()?.getItem('sessionToken') || '';
  } catch {
    return '';
  }
}

/** Build auth headers - omits X-Api-Key entirely when no token is present */
export function buildAuthHeaders(): Record<string, string> {
  const token = getSessionToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token && token !== 'local-dev-bypass') {
    headers['X-Api-Key'] = token;
  }
  return headers;
}

export function attachSearchUrl(searchUrl?: string): string | undefined {
  const normalized = searchUrl?.trim();
  // Resolve via payload -> environment -> default
  const envCandidate =
    (typeof globalThis !== 'undefined' &&
      (globalThis as any).process?.env?.SOLR_BASE_URL) ||
    (typeof window !== 'undefined' && (window as any).SOLR_BASE_URL) ||
    environment.voyager.solrSelectUrl;

  const candidate = normalized || String(envCandidate || '').trim();
  if (!candidate) return undefined;

  // Bind on first session use. If already bound to the same value, allow reuse
  if (boundSearchUrl === null) {
    boundSearchUrl = candidate;
    try {
      // Helpful debug when diagnosing missing payload fields
      // eslint-disable-next-line no-console
      console.debug('[VES] Binding searchUrl for session:', boundSearchUrl);
    } catch {}
    return candidate;
  }

  if (boundSearchUrl === candidate) {
    return candidate;
  }

  return undefined;
}

/**
 * Silently attempt to get a session JWT from the verify endpoint
 * without a Turnstile token - used when CAPTCHA is disabled.
 *
 * Returns true if a token was successfully obtained and stored.
 * Returns false if the backend doesn't support anonymous sessions.
 */
export async function fetchAnonymousSession(
  verificationBaseUrl: string,
): Promise<boolean> {
  const baseUrl = verificationBaseUrl.replace(/\/$/, '');
  try {
    const res = await fetch(`${baseUrl}/verify-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bypass: true }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    const token: string | undefined = data?.session?.token;
    if (!token) return false;
    getSessionStorage()?.setItem('sessionToken', token);
    return true;
  } catch {
    return false;
  }
}

/**
 * Reset the session - call when user clicks "New Chat".
 * Optionally notifies the backend to clean up server-side state.
 */
export async function resetSession(baseUrl: string = ''): Promise<void> {
  const API_BASE_URL = baseUrl || environment.enhancedSearch.apiBaseUrl;
  const oldSessionId = sessionId;
  sessionId =
    globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
  boundSearchUrl = null;

  // Fire-and-forget: tell the backend to clean up the old session
  try {
    await fetch(`${API_BASE_URL}/chat2`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: '',
        sessionId: oldSessionId,
        action: 'end_session',
      }),
    });
  } catch {
    // Best-effort cleanup - don't block the UI
  }
}

/**
 * Fire-and-forget warmup ping to wake the AgentCore runtime.
 * Called on app load so the agent is ready when the user types.
 */
export function warmupAgent(baseUrl: string = ''): void {
  const API_BASE_URL = baseUrl || environment.enhancedSearch.apiBaseUrl;
  fetch(`${API_BASE_URL}/chat2`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: 'ping',
      sessionId:
        'warmup-' +
        (globalThis.crypto?.randomUUID?.() ||
          Math.random().toString(36).slice(2)),
    }),
  }).catch(() => {});
}

/** Force a fresh sessionId without notifying the backend. */
export function newSessionId(): void {
  sessionId =
    globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
  boundSearchUrl = null;
}

/** Read the current sessionId. */
export function getSessionId(): string {
  return sessionId;
}

/** Restore a previously snapshotted sessionId. */
export function setSessionId(id: string): void {
  sessionId = id;
  boundSearchUrl = null;
}

/** Accept a backend-returned sessionId without changing search URL binding. */
export function updateSessionId(id: string): void {
  sessionId = id;
}
